import {faDownload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Button, Table, TablePaginationConfig} from 'antd';

import {appRoute} from '../../../../../../../app-route';
import {NavigationLink} from '../../../../../../../layout/navigation-link/navigation-link';
import {PageCard} from '../../../../../../../layout/page-card/page-card';
import {Trend} from '../../../../../../../layout/trend/trend';
import {FormattedNumber} from '../../../../../../../provider/locale/formatted-number';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useRequestAspectsReport} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-hook';
import {
    ReviewAnalyticsAspectsHookType,
    ReviewAspectRecordType,
    ReviewAspectTableSortedColumnsEnum,
} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import * as styles from '../../../../../reviews-and-answers-statistics/average-response-time/catalogs-stats-table/catalogs-stats-table.scss';
import {SentimentBar} from '../../../../components/sentiment-bar/sentiment-bar';
import {useReviewsAnalysisFilter} from '../../../../reviews-analysis-hook';
import {ReviewsAnalysisFilterEnum} from '../../../../reviews-analysis-type';
import {keywordsAndTopicsPageSizeOptions} from '../../keywords-and-topics-page-const';
import {getTonalitySentimentData} from '../aspects-helper';

type PropsType = {
    reviewAnalyticsAspects: ReviewAnalyticsAspectsHookType;
};

export function AspectsTable({reviewAnalyticsAspects}: PropsType): JSX.Element {
    const {singleFilter, comparePeriodFilter, filter} = useReviewsAnalysisFilter(false);
    const {mutate: requestAspectsReport, isLoading: isRequestingReport} = useRequestAspectsReport();
    const isCompareMode = Boolean(comparePeriodFilter.startDate);

    const {data, isLoading, pagination, sorter: tableSorter} = reviewAnalyticsAspects;

    const {setSorterOrder, getSortOrder} = tableSorter;

    const {total, pageSize, page, onChange: onPaginationChange} = pagination;

    return (
        <PageCard
            headerClassName={styles.CatalogStatsTable_title}
            title={
                <>
                    <div>
                        <Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TABLE_TITLE" />
                    </div>
                    <div>
                        <Button
                            icon={<FontAwesomeIcon className={styles.CatalogStatsTable_icon} icon={faDownload} />}
                            loading={isRequestingReport}
                            onClick={() =>
                                requestAspectsReport({
                                    ...singleFilter,
                                    startDate: filter[ReviewsAnalysisFilterEnum.Range][0],
                                    endDate: filter[ReviewsAnalysisFilterEnum.Range][1],
                                    ...(isCompareMode
                                        ? {
                                              comparedStartDate: filter[ReviewsAnalysisFilterEnum.CompareRange][0],
                                              comparedEndDate: filter[ReviewsAnalysisFilterEnum.CompareRange][1],
                                          }
                                        : {}),
                                })
                            }
                        >
                            <Locale stringKey="DOWNLOAD_THE_REPORT" />
                        </Button>
                    </div>
                </>
            }
        >
            <Table<ReviewAspectRecordType>
                dataSource={data?.results || []}
                loading={isLoading}
                onChange={(_newPagination: TablePaginationConfig, _filters, sorter) => {
                    setSorterOrder(sorter);
                }}
                pagination={{
                    total,
                    pageSize,
                    pageSizeOptions: keywordsAndTopicsPageSizeOptions,
                    current: page,
                    size: 'default',
                    showSizeChanger: true,
                    onChange: onPaginationChange,
                    totalBoundaryShowSizeChanger: data?.count,
                    // eslint-disable-next-line react/no-unstable-nested-components
                    showTotal: (totalCount) => {
                        return (
                            <Text
                                large
                                stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TOTAL"
                                valueMap={{
                                    count: (
                                        <Text bolder key="count">
                                            {totalCount}
                                        </Text>
                                    ),
                                }}
                            />
                        );
                    },
                }}
                scroll={{x: true}}
                size="middle"
            >
                <Table.Column<ReviewAspectRecordType>
                    render={(_value, record) => {
                        return (
                            <NavigationLink
                                to={`${appRoute.reviewsManagementReviews.path}?ordering=-creation_date&tag-list=${record.ids}`}
                            >
                                {record.name}
                            </NavigationLink>
                        );
                    }}
                    title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NAME" />}
                />

                {isCompareMode ? (
                    <>
                        <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEGATIVE" />}>
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainNegativeReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.negativeReviews.value}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNegativeReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaNegativeReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.negativeReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaNegativeReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.compareNegativeReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.negativeReviews.comparedValue}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.compareNegativeReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>
                        <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEUTRAL" />}>
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainNeutralReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.neutralReviews.value}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNeutralReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaNeutralReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.neutralReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaNeutralReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                dataIndex={ReviewAspectTableSortedColumnsEnum.compareNeutralReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.neutralReviews.comparedValue}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.compareNeutralReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>

                        <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__POSITIVE" />}>
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainPositiveReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.positiveReviews.value}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainPositiveReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaPositiveReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.positiveReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaPositiveReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                dataIndex={ReviewAspectTableSortedColumnsEnum.comparePositiveReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.positiveReviews.comparedValue}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.comparePositiveReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>
                    </>
                ) : (
                    <>
                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainNegativeReviews}
                            render={(_value, record) => {
                                return <Text block>{record.negativeReviews.value}</Text>;
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNegativeReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEGATIVE" />}
                        />

                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainNeutralReviews}
                            render={(_value, record) => {
                                return <Text block>{record.neutralReviews.value}</Text>;
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNeutralReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEUTRAL" />}
                        />

                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainPositiveReviews}
                            render={(_value, record) => {
                                return <Text block>{record.positiveReviews.value}</Text>;
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainPositiveReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__POSITIVE" />}
                        />
                    </>
                )}

                {isCompareMode ? (
                    <>
                        <Table.ColumnGroup
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__SENTIMENT_INDEX" />}
                        >
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainTonalityIndex}
                                render={(_value, record) => {
                                    const value = record.tonalityIdx.value;

                                    return value ? (
                                        <FormattedNumber options={{maximumFractionDigits: 1}} value={value} />
                                    ) : (
                                        '—'
                                    );
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainTonalityIndex)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaTonalityIndex}
                                render={(_value, record) => {
                                    return <Trend delta={record.tonalityIdx.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaTonalityIndex)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.compareTonalityIndex}
                                render={(_value, record) => {
                                    const value = record.tonalityIdx.comparedValue;

                                    return value ? (
                                        <FormattedNumber options={{maximumFractionDigits: 1}} value={value} />
                                    ) : (
                                        '—'
                                    );
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.compareTonalityIndex)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>

                        <Table.ColumnGroup
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__MENTIONS_COUNT" />}
                        >
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainTotalReviews}
                                render={(_value, record) => {
                                    const value = record.totalReviews.value;

                                    return value ? <Text block>{value}</Text> : '—';
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainTotalReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaTotalReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.totalReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaTotalReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.compareTotalReviews}
                                render={(_value, record) => {
                                    const value = record.totalReviews.comparedValue;

                                    return value ? <Text block>{value}</Text> : '—';
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.compareTotalReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>
                    </>
                ) : (
                    <>
                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainTonalityIndex}
                            render={(_value, record) => {
                                const value = record.tonalityIdx.value;

                                return value ? (
                                    <FormattedNumber options={{maximumFractionDigits: 1}} value={value} />
                                ) : (
                                    '—'
                                );
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainTonalityIndex)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__SENTIMENT_INDEX" />}
                        />

                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainTotalReviews}
                            render={(_value, record) => {
                                const value = record.totalReviews.value;

                                return value ? <Text block>{value}</Text> : '—';
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainTotalReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__MENTIONS_COUNT" />}
                        />
                    </>
                )}

                {isCompareMode ? (
                    <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__DISTRIBUTION" />}>
                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            className={styles.CatalogStatsTable_column}
                            render={(_value, record) => {
                                return (
                                    <SentimentBar
                                        banUnspecified
                                        sentimentData={getTonalitySentimentData(record.tonalityCurrentPeriodData)}
                                    />
                                );
                            }}
                            title="A"
                        />

                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            className={styles.CatalogStatsTable_column}
                            render={(_value, record) => {
                                return (
                                    <SentimentBar
                                        banUnspecified
                                        sentimentData={getTonalitySentimentData(record.tonalityComparePeriodData)}
                                    />
                                );
                            }}
                            title="B"
                        />
                    </Table.ColumnGroup>
                ) : (
                    <Table.Column<ReviewAspectRecordType>
                        align="center"
                        render={(_value, record) => {
                            return (
                                <SentimentBar
                                    banUnspecified
                                    sentimentData={getTonalitySentimentData(record.tonalityCurrentPeriodData)}
                                />
                            );
                        }}
                        title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__DISTRIBUTION" />}
                    />
                )}
            </Table>
        </PageCard>
    );
}
